import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Building2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { errorMessage } from "../lib/api";
import { Button } from "../components/core/Button";
import { Field, Input } from "../components/core/Field";
import { PasswordInput } from "../components/core/PasswordInput";
import { AuthLayout } from "../components/shared/AuthLayout";

const REMEMBER_KEY = "enfleet.remember_email";

export default function LoginPage({ portal }: { portal: "backoffice" | "client" }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const isBO = portal === "backoffice";
  const remembered = localStorage.getItem(`${REMEMBER_KEY}.${portal}`) || "";
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(!!remembered);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(portal, email, password, remember);
      if (remember) localStorage.setItem(`${REMEMBER_KEY}.${portal}`, email.trim().toLowerCase()); else localStorage.removeItem(`${REMEMBER_KEY}.${portal}`);
      navigate(isBO ? "/bo" : "/app");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout isBO={isBO} testId={`${portal}-login-page`} eyebrow={isBO ? "Back-Office HQ" : "Fleet Console"}
      headline={isBO ? "Master control across every client fleet." : "Your fleet, your compliance, in one console."}
      bullets={isBO ? ["Onboard clients and configure co-branding", "Register vehicles and create client admins", "Monitor cross-client alerts and audit trails"] : ["Maintain vehicle records, documents and photos", "Track road tax, insurance, PUSPAKOM, permits", "Act on alerts before renewals lapse"]}>
      <form onSubmit={submit} className="card-crisp p-8 rise" data-testid={`${portal}-login-form`}>
        <div className="mb-5 flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-md ${isBO ? "bg-[#10046E] text-[color:var(--gold)]" : "bg-slate-900 text-white"}`}>{isBO ? <ShieldCheck className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}</div>
          <div><h2 className="font-display text-base font-bold text-slate-900">{isBO ? "Back-Office Sign In" : "Welcome back"}</h2><p className="text-xs text-slate-500">{isBO ? "Super Admin access only" : "Sign in to manage your fleet"}</p></div>
        </div>
        <div className="space-y-3">
          <Field label="Email"><Input data-testid="login-email-input" type="email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} required autoComplete="username" placeholder="you@company.my" /></Field>
          <Field label="Password"><PasswordInput testId="login-password-input" value={password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} required autoComplete="current-password" placeholder="••••••••" /></Field>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <label className="flex cursor-pointer items-center gap-2 text-slate-600"><input data-testid="remember-me-checkbox" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[#10046E]" />Remember me for 30 days</label>
          <Link data-testid="forgot-password-link" to={`/forgot-password?portal=${portal}`} className="font-semibold text-[color:var(--brand)] hover:underline">Forgot password?</Link>
        </div>
        {error && <p data-testid="login-error" className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{error}</p>}
        <Button data-testid="login-submit-button" type="submit" loading={busy} className="mt-5 w-full">Sign in</Button>
        {isBO && <p className="mt-4 text-center text-xs text-slate-500"><Link data-testid="switch-to-client-link" className="font-semibold text-[color:var(--brand)] hover:underline" to="/login">Fleet portal →</Link></p>}
      </form>
    </AuthLayout>
  );
}
